import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Card, CardHeader, Input, Select, Field, Table, THead, TBody, TR, TH, TD,
  StatusBadge, EmptyState, ErrorNotice, Loading, Pagination, Button, Modal, CopyField,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { http } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { Building2, Plus, KeyRound } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

const CURRENCIES = ['USD', 'INR', 'GBP', 'EUR', 'AUD', 'CAD', 'SGD', 'AED'];

/**
 * Creating an organisation on someone's behalf.
 *
 * The admin never chooses the password. One is generated and shown once, here,
 * and has to be handed over out of band — a platform that knows its customers'
 * passwords is a platform that can be compelled to produce them.
 */
function CreateOrganisationDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ businessName: '', email: '', country: 'IN', defaultCurrency: 'INR' });
  const [result, setResult] = useState(null);

  const { mutate, loading, error, reset } = useMutation(async () => {
    setResult(await http.post('/admin/merchants', form));
    onCreated?.();
  });

  const close = () => {
    setResult(null);
    setForm({ businessName: '', email: '', country: 'IN', defaultCurrency: 'INR' });
    reset();
    onClose();
  };

  /* ---- after creation: the two secrets, shown once and never again ---- */
  if (result) {
    return (
      <Modal
        open={open}
        onClose={close}
        title={`${result.merchant.name} is ready`}
        blurb="Copy both of these now. Neither can be shown again."
        footer={<Button onClick={close}>Done</Button>}
      >
        <div className="space-y-4">
          <CopyField label="Sign-in email" value={result.owner.email} />
          <CopyField label="One-time password" value={result.temporaryPassword} />
          <CopyField label="Test secret key" value={result.secretKey} />

          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-amber-800">
              Send these over a channel the owner already trusts, and ask them to change the password
              on first sign-in. We cannot retrieve any of them for you later.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add an organisation"
      blurb="Creates the account, its owner and a sandbox API key — the same as if they had signed up themselves."
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={loading}>Cancel</Button>
          <Button loading={loading} onClick={() => mutate().catch(() => {})}>Create organisation</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}>
        {error && <ErrorNotice error={error} title="Could not create this organisation" />}

        <Field label="Business name" required error={error?.fieldErrors?.businessName}>
          {(props) => (
            <Input
              {...props}
              value={form.businessName}
              onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
              placeholder="Saanvi Textiles"
              autoFocus
            />
          )}
        </Field>

        <Field
          label="Owner email"
          required
          hint="This becomes their sign-in. They can add more users themselves afterwards."
          error={error?.fieldErrors?.email}
        >
          {(props) => (
            <Input
              {...props}
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="owner@business.com"
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Country" required error={error?.fieldErrors?.country}>
            {(props) => (
              <Input
                {...props}
                value={form.country}
                maxLength={2}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value.toUpperCase() }))}
                placeholder="IN"
              />
            )}
          </Field>

          <Field
            label="Default currency"
            required
            hint="What they get if they omit one."
            error={error?.fieldErrors?.defaultCurrency}
          >
            {(props) => (
              <Select
                {...props}
                value={form.defaultCurrency}
                onChange={(e) => setForm((f) => ({ ...f, defaultCurrency: e.target.value }))}
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            )}
          </Field>
        </div>
      </form>
    </Modal>
  );
}

/** AD-2: the organisation list, with search, status filter and creation. */
export function AdminMerchantsPage() {
  /**
   * Opened straight from the top bar's + button, which navigated here with
   * `?new=1` and was then ignored — so the button did nothing at all.
   */
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(params.get('new') === '1');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [applied, setApplied] = useState('');
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => { setApplied(search.trim()); setSkip(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, error, loading, refetch } = useResource('/admin/merchants', {
    params: { limit: 25, skip, ...(applied ? { search: applied } : {}), ...(status ? { status } : {}) },
  });

  return (
    <>
      <PageHero
        chip="Platform"
        icon={Building2}
        title="Organisations"
        blurb="Every business on the platform. Open one to see its payments, providers and people — never its credentials."
        actions={<Button icon={Plus} onClick={() => setCreating(true)}>Add organisation</Button>}
      />

      <Card>
        <CardHeader
          title="All organisations"
          blurb={data ? `${data.total} total` : undefined}
          actions={
            <div className="flex gap-2">
              <div className="w-48">
                <Field>{(props) => <Input {...props} type="search" size="sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email or ID" />}</Field>
              </div>
              <Field>
                {(props) => (
                  <Select {...props} value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="h-8 text-xs">
                    <option value="">Any status</option>
                    <option value="active">Active</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="suspended">Suspended</option>
                  </Select>
                )}
              </Field>
            </div>
          }
        />

        {loading && <Loading label="Loading merchants…" />}
        {error && <ErrorNotice error={error} onRetry={refetch} className="m-4" />}
        {!loading && !error && data?.merchants?.length === 0 && (
          <EmptyState
            title={applied || status ? 'No organisations match' : 'No organisations yet'}
            blurb={applied || status
              ? 'Try a different search term, or clear the status filter.'
              : 'Add the first one, or wait for a business to sign up themselves.'}
            action={!applied && !status
              ? <Button icon={Plus} onClick={() => setCreating(true)}>Add organisation</Button>
              : undefined}
          />
        )}

        {!loading && !error && data?.merchants?.length > 0 && (
          <>
            <Table>
              <THead>
                <TR><TH>Business</TH><TH>Status</TH><TH>Country</TH><TH>Currency</TH><TH align="right">Joined</TH></TR>
              </THead>
              <TBody>
                {data.merchants.map((merchant) => (
                  <TR key={merchant.id}>
                    <TD>
                      <Link to={`/admin/merchants/${merchant.id}`} className="font-medium text-accent-ink hover:underline">
                        {merchant.name}
                      </Link>
                      <div className="text-xs text-muted">{merchant.email}</div>
                    </TD>
                    <TD><StatusBadge status={merchant.status} size="sm" /></TD>
                    <TD className="text-sm">{merchant.country}</TD>
                    <TD className="text-sm">{merchant.defaultCurrency}</TD>
                    <TD align="right" className="text-xs text-muted">{formatTimestamp(merchant.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pagination total={data.total} limit={data.limit} skip={data.skip} onChange={setSkip} />
          </>
        )}
      </Card>

      <CreateOrganisationDialog
        open={creating}
        onClose={() => {
          setCreating(false);
          // Drop the flag, or a refresh re-opens the dialog.
          if (params.get('new')) setParams({}, { replace: true });
        }}
        onCreated={refetch}
      />
    </>
  );
}

export default AdminMerchantsPage;
